
class LoadMoreButton extends HTMLElement {
  constructor() {
    super();

    this.loadMoreButtonEl = this.querySelector("a");
    this.arrowDownIconEl = this.querySelector(".icon-arrow-down");
    this.loadingSpinnerIconEl = this.querySelector(".icon-loader");

    this.loading = false;

    this.loadMoreButtonEl?.addEventListener("click", (event) => {
      event.preventDefault();
      this.loadNextPage();
    });
  }

  connectedCallback() {
    // Restore previously loaded pages only when this element
    // is first connected to the document.
    if (this.initialised) return;
    this.initialised = true;

    this.restoreLoadedPages();
  }

  get storageKey() {
    // Includes collection, filters, sorting and query parameters.
    return `ajp-load-more:${location.pathname}${location.search}`;
  }

  get savedPageCount() {
    try {
      return Number(sessionStorage.getItem(this.storageKey)) || 0;
    } catch {
      return 0;
    }
  }

  savePageCount(count) {
    try {
      sessionStorage.setItem(this.storageKey, String(count));
    } catch {
      // Storage may be unavailable.
    }
  }

  setLoading(loading) {
    this.loading = loading;

    if (this.arrowDownIconEl) {
      this.arrowDownIconEl.style.display = loading ? "none" : "block";
    }

    if (this.loadingSpinnerIconEl) {
      this.loadingSpinnerIconEl.style.display = loading ? "block" : "none";
    }

    if (this.loadMoreButtonEl) {
      this.loadMoreButtonEl.setAttribute(
        "aria-busy",
        String(loading)
      );
    }
  }

  async loadNextPage({ restoring = false } = {}) {
    if (this.loading || !this.loadMoreButtonEl) return false;

    const productGridEl = document.querySelector("#product-grid");
    const url = this.loadMoreButtonEl.getAttribute("href");

    if (!productGridEl || !url) return false;

    this.setLoading(true);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html"
      );

      const newProductGridEl = html.querySelector("#product-grid");

      if (!newProductGridEl) {
        throw new Error("Product grid missing from response");
      }

      // Append the next batch of products.
      productGridEl.insertAdjacentHTML(
        "beforeend",
        newProductGridEl.innerHTML
      );

      const nextButton = html.querySelector("load-more-button a");

      if (nextButton) {
        this.loadMoreButtonEl.setAttribute(
          "href",
          nextButton.getAttribute("href")
        );
      }

      // Count only successfully loaded pages.
      if (!restoring) {
        this.savePageCount(this.savedPageCount + 1);
      }

      if (!nextButton) {
        this.remove();
      }

      return true;
    } catch (error) {
      console.error("[AJP Load More]", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  async restoreLoadedPages() {
    const pagesToRestore = this.savedPageCount;

    if (!pagesToRestore) return;

    // Allow the initial document to finish rendering.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    for (let i = 0; i < pagesToRestore; i++) {
      if (!this.isConnected) break;

      const success = await this.loadNextPage({
        restoring: true
      });

      if (!success) break;
    }
  }
}

if (!customElements.get("load-more-button")) {
  customElements.define("load-more-button", LoadMoreButton);
}
